import { commonDateLabels } from "@/i18n/labels/commonLabels";
import { getLanguage, type Language } from "@/utils/languageUtils";

/**
 * Date formatting utilities for the application
 */

/** Format a Date as `yyyy-MM-dd` for native `<input type="date">` value/min/max. */
export function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export type DateFormatOptions = {
  /** Whether to include time in the formatted string */
  includeTime?: boolean;
  /** Whether to use short format (e.g., "Jan 15, 2024") or default format */
  shortFormat?: boolean;
  /** Custom locale for formatting (defaults to "sv-SE") */
  locale?: string;
  /** Language for fallback strings (e.g. "N/A") */
  lang?: Language;
};

function getDateLabels(lang?: Language) {
  const d = commonDateLabels(lang ?? getLanguage());
  return {
    na: d.na ?? "N/A",
    justNow: d.justNow ?? "Just nu",
    future: d.future ?? "Framtiden",
    ended: d.ended ?? "Slutad",
    yearAgo: d.yearAgo ?? "1 år sedan",
    yearsAgo: d.yearsAgo ?? "{count} år sedan",
    monthAgo: d.monthAgo ?? "1 månad sedan",
    monthsAgo: d.monthsAgo ?? "{count} månader sedan",
    weekAgo: d.weekAgo ?? "1 vecka sedan",
    weeksAgo: d.weeksAgo ?? "{count} veckor sedan",
    dayAgo: d.dayAgo ?? "1 dag sedan",
    daysAgo: d.daysAgo ?? "{count} dagar sedan",
    hourAgo: d.hourAgo ?? "1 timme sedan",
    hoursAgo: d.hoursAgo ?? "{count} timmar sedan",
    minuteAgo: d.minuteAgo ?? "1 minut sedan",
    minutesAgo: d.minutesAgo ?? "{count} minuter sedan",
    secondAgo: d.secondAgo ?? "1 sekund sedan",
    secondsAgo: d.secondsAgo ?? "{count} sekunder sedan",
  };
}

/**
 * Formats a date string or Date object to a localized string
 * @param dateInput - Date string, Date object, or null/undefined
 * @param options - Formatting options
 * @returns Formatted date string or "N/A" if invalid
 */
export const formatDate = (
  dateInput: string | Date | null | undefined,
  options: DateFormatOptions = {}
): string => {
  const {
    includeTime = false,
    shortFormat = false,
    locale = "sv-SE",
    lang
  } = options;
  const labels = getDateLabels(lang);

  if (!dateInput) return labels.na;

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return labels.na;

    if (shortFormat) {
      return date.toLocaleDateString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        ...(includeTime && {
          hour: "2-digit",
          minute: "2-digit"
        })
      });
    }

    if (includeTime) {
      return date.toLocaleDateString(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    }

    return date.toLocaleDateString(locale);
  } catch {
    return labels.na;
  }
};

/**
 * Formats a date to a short format (e.g., "Jan 15, 2024")
 * @param dateInput - Date string, Date object, or null/undefined
 * @param locale - Optional locale (e.g. "en-US", "sv-SE"). Defaults to "sv-SE".
 * @returns Formatted date string or "N/A" if invalid
 */
export const formatDateShort = (
  dateInput: string | Date | null | undefined,
  locale?: string,
  lang?: Language
): string => {
  return formatDate(dateInput, { shortFormat: true, locale: locale ?? "sv-SE", lang });
};

/**
 * Formats a date with time included
 * @param dateInput - Date string, Date object, or null/undefined
 * @returns Formatted date and time string or "N/A" if invalid
 */
export const formatDateTime = (dateInput: string | Date | null | undefined, lang?: Language): string => {
  return formatDate(dateInput, { includeTime: true, lang });
};

/**
 * Formats a date to a short format with time
 * @param dateInput - Date string, Date object, or null/undefined
 * @returns Formatted short date and time string or "N/A" if invalid
 */
export const formatDateTimeShort = (dateInput: string | Date | null | undefined, lang?: Language): string => {
  return formatDate(dateInput, { shortFormat: true, includeTime: true, lang });
};

/**
 * Calculates relative time (e.g., "2 dagar sedan", "Just nu")
 * @param dateInput - Date string, Date object, or null/undefined
 * @param lang - Optional language for labels
 * @returns Relative time string or "N/A" if invalid
 */
export const formatRelativeTime = (dateInput: string | Date | null | undefined, lang?: Language): string => {
  const labels = getDateLabels(lang);

  if (!dateInput) return labels.na;

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return labels.na;

    const now = new Date();
    const diff = now.getTime() - date.getTime();

    if (diff < 0) return labels.future;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return years === 1 ? labels.yearAgo : labels.yearsAgo.replace("{count}", String(years));
    if (months > 0) return months === 1 ? labels.monthAgo : labels.monthsAgo.replace("{count}", String(months));
    if (weeks > 0) return weeks === 1 ? labels.weekAgo : labels.weeksAgo.replace("{count}", String(weeks));
    if (days > 0) return days === 1 ? labels.dayAgo : labels.daysAgo.replace("{count}", String(days));
    if (hours > 0) return hours === 1 ? labels.hourAgo : labels.hoursAgo.replace("{count}", String(hours));
    if (minutes > 0) return minutes === 1 ? labels.minuteAgo : labels.minutesAgo.replace("{count}", String(minutes));
    if (seconds > 0) return seconds === 1 ? labels.secondAgo : labels.secondsAgo.replace("{count}", String(seconds));

    return labels.justNow;
  } catch {
    return labels.na;
  }
};

/**
 * Calculates time remaining until a future date (e.g., "2d 5h 30m", "1h 15m")
 * @param endDate - Date string or Date object for the end date
 * @param lang - Optional language for labels (N/A, Ended)
 * @returns Object with timeString and isUrgent flag
 */
export const calculateTimeRemaining = (endDate: string | Date | null | undefined, lang?: Language): { timeString: string; isUrgent: boolean } => {
  const labels = getDateLabels(lang);

  if (!endDate) return { timeString: labels.na, isUrgent: false };

  try {
    const now = new Date();
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return { timeString: labels.na, isUrgent: false };

    const diff = end.getTime() - now.getTime();

    if (diff <= 0) {
      return { timeString: labels.ended, isUrgent: false };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    let timeString: string;
    if (days > 0) {
      timeString = `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      timeString = `${hours}h ${minutes}m`;
    } else {
      timeString = `${minutes}m`;
    }

    // Check if less than 1 hour remains
    const isUrgent = diff < (1000 * 60 * 60); // Less than 1 hour

    return { timeString, isUrgent };
  } catch {
    return { timeString: labels.na, isUrgent: false };
  }
};